import { createPortal } from 'react-dom'
import { useI18n } from '../i18n'
import { X } from 'lucide-react'
import QRCodeDisplay from '../utils/qrcode'
import orellaWordmarkWhite from '../assets/logos/orella-wordmark-white-transparent.png'

/**
 * SharePassModal — the "Case Report" pass for the patient's share code.
 *
 * Visual sibling of WalletPassModal (the check-in Visit Pass): same navy
 * wallet-style card, "Case Report" pill, dashed perforation, field stack, and
 * white QR tile. The patient flashes this at the front desk; the office scans
 * the QR (which encodes the claim URL) and lands on the case.
 *
 * Path A (interim): rendered as an in-app popup, like the check-in pass.
 * Path B (post-launch): becomes a real Apple Wallet .pkpass.
 *
 * Props:
 *   isOpen, onClose
 *   patientName, caseLine, shareCode, expiresLabel
 *   qrValue      — the URL the office scans (claim URL)
 *   instruction  — office-facing caption (translated upstream)
 */

const NAVY = '#15354F'
const LIGHT_BLUE = '#A7D9FC'

function Field({ label, value, mono }) {
  return (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-wider mb-0.5" style={{ color: LIGHT_BLUE }}>
        {label}
      </div>
      <div className={`text-white text-[15px] font-semibold ${mono ? 'font-mono tracking-[2px]' : ''}`}>
        {value || '\u2014'}
      </div>
    </div>
  )
}

export default function SharePassModal({
  isOpen,
  onClose,
  patientName,
  caseLine,
  shareCode,
  expiresLabel,
  qrValue,
  instruction,
}) {
  const { t } = useI18n()
  if (!isOpen) return null

  return createPortal(
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      <div className="relative w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
        {/* Close */}
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute -top-11 right-0 p-2 rounded-lg bg-white/20 hover:bg-white/30 transition-colors"
        >
          <X className="w-5 h-5 text-white" />
        </button>

        {/* Wallet card */}
        <div className="rounded-[26px] overflow-hidden shadow-2xl" style={{ backgroundColor: NAVY }}>
          {/* Header */}
          <div className="px-6 pt-6 pb-4 flex items-center justify-between">
            <img
              src={orellaWordmarkWhite}
              alt="Orella"
              className="h-6 w-auto select-none"
              draggable={false}
            />
            <span
              className="text-[11px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full"
              style={{ color: NAVY, backgroundColor: LIGHT_BLUE }}
            >
              {t('diagnose.visitPrepLabel')}
            </span>
          </div>

          {/* Perforation divider */}
          <div className="mx-6 border-t border-dashed" style={{ borderColor: 'rgba(167,217,252,0.35)' }} />

          {/* Details */}
          <div className="px-6 py-5 space-y-4">
            <Field label="Patient" value={patientName} />
            <div className="grid grid-cols-2 gap-4">
              <Field label="Case" value={caseLine} />
              <Field label="Expires" value={expiresLabel} />
            </div>
            <Field label="Share code" value={shareCode} mono />
          </div>

          {/* QR tile */}
          <div className="px-6 pb-6">
            <div className="rounded-2xl bg-white p-5 flex flex-col items-center">
              {qrValue ? (
                <>
                  <QRCodeDisplay value={qrValue} size={170} color={NAVY} />
                  <p className="mt-3 text-xs text-slate-500">
                    {t('share.shareCodeLabel', 'Share code:')}{' '}
                    <span className="font-bold font-mono" style={{ color: NAVY }}>{shareCode}</span>
                  </p>
                </>
              ) : (
                <div className="text-sm text-slate-500 py-10 text-center">
                  {t('share.codeUnavailable', 'Code unavailable. Pull to refresh and try again.')}
                </div>
              )}
            </div>
            <p className="text-center text-[12px] mt-3" style={{ color: LIGHT_BLUE }}>
              {instruction}
            </p>
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}
